import { computed } from 'vue';

import {
  Day,
  CalendarDayClasses,
  CalendarTimeClasses,
  DayAvailability,
  GridTemplateColumns,
  DateWorkingMode,
} from '../models/entities/calendar.model';

import CalendarService from '../services/calendar.service';
import DateService from '../services/date.service';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useCalendar = (props, { emit }) => {
  // ----------------------------------------
  // variables
  const daysInRow = 7;

  // ----------------------------------------
  // computed
  const availableSheets = computed((): string[] => {
    const month: string[] = [];
    for (let index = 0; index < props.pages; index += 1) {
      const activeMonth = DateService.firstDayOfMonth({
        year: props.startDate.getFullYear(),
        month: props.startDate.getMonth() + index,
      });

      const options: Intl.DateTimeFormatOptions = {
        month: 'long',
        year: 'numeric',
      };
      const selectedMonth = DateService.formatDate(
        props.locale,
        activeMonth,
        options
      );
      month.push(selectedMonth);
    }
    return month;
  });

  const calendarStyle = computed((): GridTemplateColumns => {
    const columns = props.daysInRow * props.weeksInRow;
    return {
      'grid-template-columns': `repeat(${columns}, 1fr)`,
    };
  });

  const days = computed((): Day[] => {
    const calendarMonthIndex = props.startDate.getMonth();
    const calendarMonthStartDate = DateService.firstDayOfMonth({
      year: props.startDate.getFullYear(),
      month: calendarMonthIndex + props.activeCalendarPage,
    });
    return CalendarService.getCalendar(
      props.locale,
      calendarMonthStartDate,
      props.selectedDates,
      daysInRow,
      props.weeksInRow,
      props.rowsInSheet,
      props.dealerCalendar,
      props.weekStartsOn
    );
  });

  const weekDayNames = computed((): string[] => {
    return days.value
      .slice(0, daysInRow * props.weeksInRow)
      .map(({ value }: Day) =>
        DateService.formatDate(props.locale, value, { weekday: 'narrow' })
      );
  });

  // ----------------------------------------
  // methods
  const calendarDayClasses = (day): CalendarDayClasses => {
    return {
      'otb-calendar-day--start-boundary':
        day.selected && isStartBoundary(day.datetime),
      'otb-calendar-day--end-boundary':
        day.selected && isEndBoundary(day.datetime),
      'otb-calendar-day--no-control': day.selected && !!noControl(day.datetime),
      'otb-calendar-day--within-range': day.inBetween,
      'otb-calendar-day--closed-dealer': day.closed,
      'otb-calendar-day--weekend': isWeekend(day.value),
    };
  };

  const calendarTimeClasses = (day): CalendarTimeClasses => {
    return {
      'wb-type-hint-strong': isWeekend(day.value),
      'otb-calendar-day--closed': day.closed,
      'otb-calendar-day--unavailable': !day.closed && !day.enabled,
      'otb-calendar-day--pre-available': day.enabledNotInMonth,
      'otb-calendar-day--not-in-month-unavailable': day.disabledNotInMonth,
      'otb-calendar-day--current wb-type-hint-strong': day.currentDay,
    };
  };

  const changeDay = (
    day: Date,
    { closed, disabled }: DayAvailability
  ): void => {
    if (closed || disabled) return;
    emit('changeDate', day);
  };

  const getShortMonth = DateService.getShortMonth;

  /**
   * Checks if is the endDate,
   *  if both selected dates are not on same day or
   *  if the start date is not undefined;
   * If the conditions above are satisfied then add this class to draw a range background
   */
  const isEndBoundary = (actualDateTime: string): boolean => {
    // If start date is undefined then
    // place the same as the end so it will invalidate the condition
    return (
      !DateService.isVBSDateEqual(
        props.selectedDates?.start || props.selectedDates.end,
        props.selectedDates.end
      ) &&
      DateService.formatDateForVBS(props.selectedDates.end) === actualDateTime
    );
  };

  /**
   * Checks if is the startDate,
   *  if both selected dates are not on same day or
   *  if the end date is not undefined;
   * If the conditions above are satisfied then add this class to draw a range background
   */
  const isStartBoundary = (actualDateTime: string): boolean => {
    // If end date is undefined then
    // place the same as the end so it will invalidate the condition
    return (
      !DateService.isVBSDateEqual(
        props.selectedDates.start,
        props.selectedDates?.end || props.selectedDates.start
      ) &&
      DateService.formatDateForVBS(props.selectedDates.start) === actualDateTime
    );
  };

  const isWeekend = (actualDate: Date): boolean => {
    if (!actualDate) return false;
    return !(actualDate.getDay() % 6);
  };

  /**
   * Draw a diferent boundary style on the selected date that is not the working mode
   */
  const noControl = (actualDateTime: string): string | boolean => {
    return (
      !DateService.isVBSDateEqual(
        props.selectedDates.start,
        props.selectedDates.end
      ) &&
      ((props.rangeMode.workingMode === DateWorkingMode.START &&
        isEndBoundary(actualDateTime)) ||
        (props.rangeMode.workingMode === DateWorkingMode.END &&
          isStartBoundary(actualDateTime)))
    );
  };

  return {
    availableSheets,
    calendarDayClasses,
    calendarTimeClasses,
    calendarStyle,
    changeDay,
    days,
    getShortMonth,
    isEndBoundary,
    isStartBoundary,
    isWeekend,
    noControl,
    weekDayNames,
  };
};

export default useCalendar;
